import { ref } from 'vue'
import { useGlobalStore } from '@/stores/globalStore'
import { useApiRoutes } from '@/lib/utils/useApiRoutes'
import { useAPI } from '@/lib/utils/useAPI'
import { buildUser } from '@/lib/utils/buildUser'
import {
  FaroEvents,
  emitError,
  emitEvent,
} from '@/lib/observability/events'
import type { AxiosResponse } from 'axios'
import type { ImpersonationState, User } from '@/lib/types'

// `buildUser` re-exported so existing consumers (`globalStore`,
// callers that imported from `useAuth`) keep their import paths
// stable. New code should import from `@/lib/utils/buildUser`.
export { buildUser }

/**
 * Composable wrapping the auth lifecycle. Stateful: `apiProcessing`
 * is a local ref tracking in-flight requests so login forms can
 * disable the submit button without per-form state.
 *
 *   const { attemptLogin, attemptLogout, checkAuth, apiProcessing } = useAuth()
 */
export const useAuth = () => {
  const globalStore = useGlobalStore()
  const apiProcessing = ref<boolean>(false)
  const routes = useApiRoutes()

  const attemptLogin = async (email: string, password: string): Promise<void> => {
    apiProcessing.value = true
    // Email is PII — NEVER tag with it. Only emit the attempt event
    // itself; session correlation in Loki comes from Faro's
    // session_id, not from event attributes.
    emitEvent(FaroEvents.AUTH_LOGIN_ATTEMPT)
    try {
      // Pre-fetch Sanctum's XSRF cookie BEFORE the login POST so
      // axios derives `X-XSRF-TOKEN` from `document.cookie` on the
      // submission. The endpoint sits at /sanctum/csrf-cookie (not
      // under /api/), so it goes through `useAPI().ensureCsrfCookie()`
      // — NOT a regular `routes.*` entry.
      await useAPI().ensureCsrfCookie()
      // A fresh login replaces any prior locked-out state so the
      // post-submit success path can issue follow-up requests.
      useAPI().resumeAfterLockout()

      const user = await routes.LOGIN.post<{ email: string; password: string }, User>()
        .notifyOnError(true)
        .send({ email, password })
      if (user) {
        globalStore.user = buildUser(user)
        emitEvent(FaroEvents.AUTH_LOGIN_SUCCESS)
      }
    } catch (err) {
      // Login failures are the highest-signal anonymous traffic on the
      // wire — surface them with the server's status code so dashboards
      // can chart "wrong credentials" vs "throttled" vs "server down"
      // separately. The server's message body is the failure REASON
      // (e.g. "These credentials do not match our records.") — that's
      // worth keeping in the event log; it's NOT PII.
      //
      // Notification of the user is NOT this layer's concern — the
      // sendable's `.notifyOnError(true)` handles it from inside the
      // HTTP layer, where the status-code → user-message mapping
      // lives. `useAuth` only emits observability events.
      const axiosResp = err as AxiosResponse | undefined
      emitEvent(FaroEvents.AUTH_LOGIN_FAILED, () => ({
        status: axiosResp?.status !== undefined ? String(axiosResp.status) : 'none',
        reason: (axiosResp?.data as { message?: string } | undefined)?.message ?? 'unknown',
      }))
      throw err
    } finally {
      apiProcessing.value = false
    }
  }

  const attemptLogout = async (): Promise<void> => {
    apiProcessing.value = true
    emitEvent(FaroEvents.AUTH_LOGOUT)
    try {
      await routes.LOGOUT.post().notifyOnError(false).send()
    } catch (err) {
      // Logout endpoint failures don't block the local clear — the
      // session is dead either way. Capture the failure for ops
      // visibility but proceed to clearAuth regardless.
      emitError(err, () => ({ phase: 'attemptLogout' }))
    } finally {
      // Centralized clear — the store owns "clear auth state".
      // Triggers the user-watcher (cancel in-flight, route to Login).
      globalStore.clearAuth()
      apiProcessing.value = false
    }
  }

  /**
   * Re-read impersonation state from the server. Silent by design: a failure
   * here must never block the app, and "unknown" is treated as "not
   * impersonating".
   */
  const refreshImpersonation = async (): Promise<void> => {
    try {
      const state = await routes.IMPERSONATION.get<ImpersonationState>()
        .redirectOnAuthFailed(false)
        .notifyOnError(false)
        .send()
      if (state) {
        globalStore.impersonation = state
      }
    } catch {
      /* leave the last known state; the banner is not worth an error toast */
    }
  }

  /**
   * Fetch the current authed user from the server. Used:
   *  - on first paint (route guard, before any page renders)
   *  - on hard refresh (session cookie may still be valid)
   *
   * `shouldRedirect=true` (default) → on 401, the API client
   * already redirects to Login. `false` → silent; caller handles.
   * Returns true iff a user was loaded.
   */
  const checkAuth = async (shouldRedirect = true): Promise<boolean> => {
    if (globalStore.user.detail) {
      return true
    }
    try {
      globalStore.appLoading = true
      const user = await routes.AUTH_ME.get<User>()
        .redirectOnAuthFailed(shouldRedirect)
        .notifyOnError(false)
        .send()
      if (user) {
        globalStore.user = buildUser(user)
        // Server-owned: an impersonated session must reveal itself on every
        // hydration, or a hard refresh would leave the operator inside
        // someone else's account with no visible way back.
        await refreshImpersonation()
        emitEvent(FaroEvents.AUTH_HYDRATE_SUCCESS)
        return true
      }
      emitEvent(FaroEvents.AUTH_HYDRATE_NO_SESSION)
      return false
    } catch (err) {
      // 401 here is the expected "no session" path; only push as an
      // error event when the failure was NOT a clean unauthenticated
      // response (network failure, server error, etc.).
      const axiosResp = err as AxiosResponse | undefined
      if (axiosResp?.status === 401) {
        emitEvent(FaroEvents.AUTH_HYDRATE_NO_SESSION)
      } else {
        emitError(err, () => ({ phase: 'checkAuth' }))
      }
      return false
    } finally {
      globalStore.appLoading = false
    }
  }

  /**
   * Become `userId` (platform super_admin only; every rule is enforced
   * server-side). The response carries the target's user payload, so the
   * in-memory user is swapped without a follow-up fetch.
   *
   * Cached tenant-scoped state is dropped first: the two identities see
   * different data, and stale dashboard/resource state from the operator's
   * own session must not bleed into the customer's view.
   */
  const startImpersonation = async (userId: number): Promise<boolean> => {
    apiProcessing.value = true
    try {
      const user = await routes.IMPERSONATION.post<undefined, User>(String(userId))
        .notifyOnError(true)
        .send()
      if (!user) {
        return false
      }
      globalStore.user = buildUser(user)
      await refreshImpersonation()
      return true
    } catch {
      return false
    } finally {
      apiProcessing.value = false
    }
  }

  /** Revert to your own account. */
  const stopImpersonation = async (): Promise<boolean> => {
    apiProcessing.value = true
    try {
      // Empty resourceId on purpose: the stop endpoint takes no target (it
      // reads the parked operator off the session, so it works even if the
      // impersonated account was suspended). `resolveUri` drops an empty id,
      // yielding `DELETE /api/auth/impersonation`.
      const user = await routes.IMPERSONATION.delete<User>('')
        .notifyOnError(true)
        .send()
      if (!user) {
        return false
      }
      globalStore.user = buildUser(user)
      await refreshImpersonation()
      return true
    } catch {
      return false
    } finally {
      apiProcessing.value = false
    }
  }

  return {
    apiProcessing,
    attemptLogin,
    attemptLogout,
    checkAuth,
    refreshImpersonation,
    startImpersonation,
    stopImpersonation,
  }
}
